/**
 * @file Header 积分指示器
 * @description 极简版积分显示组件，用于导航栏。
 *
 * @features
 * - 使用 @readsavor/credits 的 `useCreditBalance` Hook 作为唯一数据源。
 * - 仅显示用户剩余的总积分。
 * - 点击可跳转到个人资料页面。
 * - 在加载或出错时，组件不渲染。
 */
import React from 'react';
import { useCreditBalance } from '@readsavor/credits';
import { TransitionLink } from '../../routes/TransitionLink.js';
import { getRoutePath, RouteNames } from '../../routes/index.js';

interface CreditIndicatorProps {
  className?: string;
}

/**
 * 格式化积分数字，使用千位分隔符。
 * @param credits - 要格式化的数字。
 * @returns 格式化后的字符串。
 */
const formatCredits = (credits: number): string => {
  return credits.toLocaleString('en-US');
};

export const CreditIndicator: React.FC<CreditIndicatorProps> = ({ className }) => {
  const { data, isLoading, error } = useCreditBalance();

  // 从响应数据中获取总剩余积分
  const creditsRemaining = data?.total.totalBalance;

  // 在加载、错误或积分数据无效时，不显示组件
  if (isLoading || error || typeof creditsRemaining !== 'number') {
    return null;
  }

  const combinedClassName = [
    'py-2 rounded-md text-base font-medium text-interactive-emerald-flash hover:text-interactive-sunset-orange hover:bg-white hover:bg-opacity-10 transition-all',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TransitionLink to={getRoutePath(RouteNames.PROFILE)} className={combinedClassName}>
      <span>{formatCredits(creditsRemaining)}</span>
    </TransitionLink>
  );
};

export default CreditIndicator;
